import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview'
import type { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig'
import { Image, ImageInsert, AutoImage, ImageConfig } from '@ckeditor/ckeditor5-image'
import type UploadImageCommand from '@ckeditor/ckeditor5-image/src/imageupload/uploadimagecommand'
import InsertImageCommand from '@ckeditor/ckeditor5-image/src/image/insertimagecommand'
import { MenuBarMenuListItemButtonView } from '@ckeditor/ckeditor5-ui'
import { Locale } from '@ckeditor/ckeditor5-utils'
import FileRepository from '@ckeditor/ckeditor5-upload/src/filerepository'
import { UluleUploadAdapter, UluleUploadAdapterOptions } from './UluleUploadAdapter'
import { icons } from '@ckeditor/ckeditor5-core'

export class UluleImageInsert extends Plugin {
  static get requires(): [typeof Image, typeof AutoImage, typeof ImageInsert, typeof UluleImageInsertUI] {
    return [Image, AutoImage, ImageInsert, UluleImageInsertUI]
  }
}

export class UluleImageInsertUI extends Plugin {
  private onUploadFailure?: () => void
  private onUploadSuccess?: () => void

  init(): void {
    const editor = this.editor
    const uploadConfig = this._getUploadConfig()

    if (!uploadConfig) {
      return
    }

    const uploadImageCommand = editor.commands.get('uploadImage')
    if (uploadImageCommand === undefined) {
      throw Error('no command uploadImage found')
    }

    editor.ui.componentFactory.add('uploadImage', () => this._createToolbarButton(uploadImageCommand))
    editor.ui.componentFactory.add('imageUpload', () => this._createToolbarButton(uploadImageCommand))
    editor.ui.componentFactory.add('menuBar:uploadImage', () =>
      this._createMenuBarButton(uploadImageCommand, 'standalone'),
    )
    if (editor.plugins.has('ImageInsertUI')) {
      editor.plugins.get('ImageInsertUI').registerIntegration({
        name: 'ulule',
        observable: () => uploadImageCommand,
        buttonViewCreator: () => this._createToolbarButton(uploadImageCommand),
        formViewCreator: () => this._createDropdownButton(uploadImageCommand),
        menuBarButtonViewCreator: (isOnly) =>
          this._createMenuBarButton(uploadImageCommand, isOnly ? 'insertOnly' : 'insertNested'),
      })
    }
    editor.plugins.get(FileRepository).createUploadAdapter = (loader) => {
      return new UluleUploadAdapter(loader, {
        upload: uploadConfig.upload,
        onUploadFailure: () => {
          if (this.onUploadFailure) {
            this.onUploadFailure()
          }
        },
        onUploadSuccess: () => {
          if (this.onUploadSuccess) {
            this.onUploadSuccess()
          }
        },
      })
    }
  }

  _createMenuBarButton(
    command: UploadImageCommand,
    type: 'standalone' | 'insertOnly' | 'insertNested',
  ): MenuBarMenuListItemButtonView {
    const t = this.editor.locale.t
    const button = this._createButton(command, MenuBarMenuListItemButtonView)
    button.withText = true
    switch (type) {
      case 'standalone':
        button.label = t('Image from computer')
        break
      case 'insertOnly':
        button.label = t('Image')
        break
      case 'insertNested':
        button.label = t('From computer')
        break
    }
    return button
  }

  _createDropdownButton(command: UploadImageCommand): ButtonView {
    const t = this.editor.locale.t
    const imageInsertUI = this.editor.plugins.get('ImageInsertUI')

    const button = this._createButton(command, ButtonView)
    button.withText = true
    button
      .bind('label')
      .to(imageInsertUI, 'isImageSelected', (isImageSelected) =>
        isImageSelected ? t('Replace from computer') : t('Upload from computer'),
      )
    button.on('execute', () => {
      if (imageInsertUI.dropdownView) {
        imageInsertUI.dropdownView.isOpen = false
      }
    })
    return button
  }

  _createToolbarButton(command: UploadImageCommand): ButtonView {
    const t = this.editor.locale.t
    const imageInsertUI = this.editor.plugins.get('ImageInsertUI')

    const button = this._createButton(command, ButtonView)
    button.tooltip = true
    button
      .bind('label')
      .to(imageInsertUI, 'isImageSelected', command, 'isAccessAllowed', (isImageSelected, isAccessAllowed) => {
        if (!isAccessAllowed) {
          return t('You have no image upload permissions.')
        }
        return isImageSelected ? t('Replace image from computer') : t('Upload image from computer')
      })
    return button
  }

  _createButton(
    command: UploadImageCommand,
    ButtonClass: { new (locale: Locale): MenuBarMenuListItemButtonView },
  ): MenuBarMenuListItemButtonView
  _createButton(command: UploadImageCommand, ButtonClass: { new (locale: Locale): ButtonView }): ButtonView
  _createButton(
    command: UploadImageCommand,
    ButtonClass: { new (locale: Locale): MenuBarMenuListItemButtonView } | { new (locale: Locale): ButtonView },
  ): MenuBarMenuListItemButtonView | ButtonView {
    const locale = this.editor.locale
    const t = this.editor.locale.t

    const buttonView = new ButtonClass(locale)

    buttonView.set({
      label: t('Upload from computer'),
      icon: icons.imageUpload,
      tooltip: true,
    })

    buttonView.bind('isEnabled').to(command)

    this.listenTo(buttonView, 'execute', () => {
      this.openModal()
    })

    return buttonView
  }

  public openModal(): void {
    const uploadConfig = this._getUploadConfig()

    if (uploadConfig === undefined) {
      return
    }

    const insertImageCommand = this.editor.commands.get('insertImage')
    if (insertImageCommand === undefined) {
      throw Error('no command insertImage found')
    }

    const uploadImageCommand = this.editor.commands.get('uploadImage')
    if (uploadImageCommand === undefined) {
      throw Error('no command uploadImage found')
    }

    uploadConfig.setCommands({
      insertImage: (params) => {
        insertImageCommand.execute(params)
      },
      uploadImage: (params) => {
        return new Promise<void>((resolve, reject) => {
          this.onUploadFailure = reject
          this.onUploadSuccess = resolve
          uploadImageCommand.execute(params)
        })
      },
    })
  }

  private _getSafeGlobalConfig(): EditorConfig {
    // HACK: we can't use `this.editor.config.get(...) because
    // internally data is cloned by lodash, and this don't work well with
    // functions. We are forced to use the private/secret `config._config`
    // property to access the function without cloning.
    //@ts-expect-error: hack
    return this.editor.config._config
  }

  private _getUploadConfig(): EditorConfig['uploadImage'] {
    return this._getSafeGlobalConfig().uploadImage
  }
}

export type UploadImageCommands = {
  insertImage: (...params: Parameters<InsertImageCommand['execute']>) => void
  uploadImage: (...params: Parameters<UploadImageCommand['execute']>) => Promise<void>
}

declare module '@ckeditor/ckeditor5-core/src/editor/editorconfig' {
  interface EditorConfig {
    uploadImage?: {
      setCommands: (commands: UploadImageCommands) => void
      upload: UluleUploadAdapterOptions['upload']
    }
    image?: ImageConfig
  }
}
